import React from 'react';
import clsx from 'clsx';
import millify from 'millify';
import dynamic from 'next/dynamic';
import { gql } from '@soundxyz/gql-string';
import type { CollectButtonSource } from '@soundxyz/utils/analytics';
import { ERROR_TYPE, MINTING_ERROR_ACTIONS, PILLARS } from '@soundxyz/utils/constants/logging';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { CollectButtonV3 } from '@components/CollectButton/CollectButtonV3';
import { GiftingButton } from '@components/Gifting/GiftingButton';
import { ALLOWED_INTERFACES_FOR_GIFTING } from '@components/Gifting/GiftingDropdown';
import { PanelWrapper } from '@components/Song/MintComponents/MintComponent/PanelComponents/Wrapper';
import { NetworkIndicator } from '@components/Song/MintComponents/NetworkIndicator';
import { Button } from '@components/main-components/button/Button';
import { ReleasesAllowlistModal } from '@components/modals/AllowlistModal/ReleaseAllowlistModal';
import { BuyArtistContractModal } from '@components/modals/Buy';
import { CheckoutModal } from '@components/modals/Buy/Checkout/CheckoutModal';
import {
  getCollectionTypeForMarketplace,
  MarketPlaceCheckoutModal,
} from '@components/modals/Buy/MarketPlaceCheckout';
import { useAuth } from '@context/auth/base';
import { ModalContext, ModalType } from '@context/modal';
import { ToastContext } from '@context/toast';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment, RegularMintInfoFragmentDoc } from '@graphql/reactQuery';
import { useLogError } from '@hooks/useLogErrorNew';
import { useMarketPlace } from '@hooks/useMarketPlace';
import { SPACE } from '@utils/constants';
import { getSingularOrPlural } from '@utils/helpers';

import { getMarketPlaceUrl } from '@utils/url';
import { SocialProofBar } from '../SocialProof';
import { SongInfo } from '../SongInfo';
import { MINT_COMPONENT_HEADING_SUBTEXT } from '../constants';
import { EditionBadges } from './Badge';
import { useMintComponent } from './Context';
import { MintComponentPanelSubInfo } from './PanelSubInfo';
import { SaleToggle } from './SaleToggle';

const SecondaryBadge = dynamic(
  () =>
    import('@components/basics/Badges/UrgencyBadge/SecondaryBadge').then(mod => mod.SecondaryBadge),
  {
    ssr: false,
  },
);

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment RegularMintInfo on Release {
    id
    type
    contractAddress
    auctionContractType
    title
    chainId
    finalQuantity
    price
    marketPlaceUrl
    samAddress
    ...BondingCurveMintData
    ...CheckoutReleaseInfo
    ...MintComponentSongInfo
    ...MarketplaceCheckout
  }
`);

//TODO: Add tests : https://linear.app/sound/issue/ENG-7405/add-the-rest-of-tests-for-mint-component
export function EditionMintComponent({
  releaseInfo,
  collectButtonSource,
  isInModal,
}: {
  releaseInfo: FragmentType<RegularMintInfoFragmentDoc>;
  collectButtonSource: CollectButtonSource;
  isInModal?: boolean;
}) {
  const { isAuthenticatedWithAPI } = useAuth();
  const logError = useLogError();
  const release = getFragment(RegularMintInfoFragmentDoc, releaseInfo);
  const [selectedPanel, setSelectedPanel] = React.useState<'PUBLIC' | 'PRESALE'>('PUBLIC');

  const isSamEnabled = !!release.samAddress;
  const {
    id: releaseId,
    chainId,
    finalQuantity,
    contractAddress,
    auctionContractType,
    type: releaseType,
    title: releaseTitle,
  } = release;
  const { openModal } = ModalContext.useContainer();
  const { openToast } = ToastContext.useContainer();
  const {
    presaleDetails,
    publicSaleDetails,
    isOpenEdition,
    isFixedEdition,
    loading: isMintComponentLoading,
    totalMinted,
    OEMinNotHit,
    publicSaleSchedule,
    presaleSchedule,
    rangeExpiryCheck,
  } = useMintComponent();

  const userHitMaxLimit = React.useMemo(() => {
    switch (selectedPanel) {
      case 'PUBLIC': {
        // sale is running
        if (publicSaleDetails.saleOngoing) {
          // based on if they can mint more or not we show the button
          return !publicSaleDetails.canMintPublicSale;
        }
        return true;
      }
      case 'PRESALE': {
        // sale is running
        if (presaleDetails?.saleOngoing) {
          // user does not have access to presale, so we disable the button
          if (!presaleDetails.hasAccess) return true;

          // based on if they can mint more or not we show the button
          return !presaleDetails.canMintPresale;
        }

        return true;
      }
      default:
        // by default we disable the button
        return true;
    }
  }, [
    presaleDetails?.canMintPresale,
    presaleDetails?.hasAccess,
    presaleDetails?.saleOngoing,
    publicSaleDetails.canMintPublicSale,
    publicSaleDetails.saleOngoing,
    selectedPanel,
  ]);

  const selectedSaleSchedule = React.useMemo(() => {
    switch (selectedPanel) {
      case 'PRESALE':
        return presaleSchedule;
      case 'PUBLIC':
        return publicSaleSchedule;
      default:
        return null;
    }
  }, [presaleSchedule, publicSaleSchedule, selectedPanel]);

  const hasPresale = !!presaleDetails;

  const selectedSchedule = React.useMemo(() => {
    switch (selectedPanel) {
      case 'PRESALE':
        return presaleDetails;
      case 'PUBLIC':
        return publicSaleDetails;
      default:
        return null;
    }
  }, [presaleDetails, publicSaleDetails, selectedPanel]);

  const collectionType = getCollectionTypeForMarketplace({ auctionContractType, releaseType });
  const { isLoading: isMarketPlaceLoading, marketPlaceFloorPriceInEth } = useMarketPlace({
    releaseId,
    contractAddress: contractAddress,
    contractChainId: chainId,
    quantity: 0,
    releaseTitle,
    collectionType,
    marketplaceUrl: release.marketPlaceUrl,
  });

  const isLoading = isMintComponentLoading || isMarketPlaceLoading;

  const openAllowlistModal = useStableCallback(() => {
    return openModal(ModalType.RELEASE_ALLOWLIST, {
      body: (
        <ReleasesAllowlistModal
          releaseId={releaseId}
          merkleRoot={presaleDetails?.merkleRoot || null}
        />
      ),
    });
  });

  const maxSupply = finalQuantity ?? publicSaleDetails.maxMintableLower;

  // Marketplace button related logic
  const marketPlaceUrl = getMarketPlaceUrl(release.marketPlaceUrl, contractAddress, chainId);

  /**
   * - user has hit max limit and public sale has started
   * - sale is sold out
   * - sale has ended
   */
  const isSaleOverOrUserHitLimit =
    selectedSchedule?.isSoldOut ||
    selectedSchedule?.saleEnded ||
    // userHitMaxLimit defaults to true if publicAddress is null, so verifying isAuthenticatedWithAPI is critical
    (userHitMaxLimit && isAuthenticatedWithAPI && publicSaleDetails.saleOngoing);

  /**
   * Sale is over and we we have listing on secondary we can let users mint secondary on sound
   */
  const showPurchaseSecondaryListings =
    Boolean(marketPlaceFloorPriceInEth) && isSaleOverOrUserHitLimit;

  const priceDisplayedToUser = showPurchaseSecondaryListings
    ? marketPlaceFloorPriceInEth
    : selectedSchedule?.formattedPrice;

  // if open edition does not hit min on cutoff time, we treat as fixed
  const publicSaleCutOff = OEMinNotHit && rangeExpiryCheck;

  const { heading } = getMintInfoText({
    isOpenEdition,
    isFixedEdition,
    isSamEnabled,
  });

  const isSoldOut = (() => {
    return publicSaleDetails.isSoldOut || publicSaleDetails.saleEnded;
  })();

  const onBuy = useStableCallback(() => {
    switch (auctionContractType) {
      case 'ARTIST':
        return openModal(ModalType.BUY_NFT, {
          body: showPurchaseSecondaryListings ? (
            <MarketPlaceCheckoutModal
              releaseData={release}
              contractAddress={contractAddress}
              marketPlacePlaceUrl={marketPlaceUrl}
              openGiftingDropdown={false}
            />
          ) : (
            <BuyArtistContractModal releaseId={releaseId} />
          ),
        });
      case 'EDITION':
        return openModal(ModalType.RELEASE_CHECKOUT, {
          body: showPurchaseSecondaryListings ? (
            <MarketPlaceCheckoutModal
              key={`${releaseId}-checkout-secondary`}
              releaseData={release}
              contractAddress={contractAddress}
              marketPlacePlaceUrl={marketPlaceUrl}
              openGiftingDropdown={false}
            />
          ) : (
            <CheckoutModal
              key={`${releaseId}-checkout`}
              releaseData={release}
              schedule={selectedSaleSchedule}
              headingText={heading}
              selectedPanel={selectedPanel}
              openGiftingDropdown={false}
            />
          ),
        });
      default:
        logError({
          action: MINTING_ERROR_ACTIONS.EDITION_MINT_ERROR,
          error: new Error(`Contract type ${auctionContractType} not supported`),
          toast: 'Error while collecting edition C',
          message: `Errored while collecting, contract type ${auctionContractType} not supported`,
          errorType: ERROR_TYPE.EDITION_MINT,
          indexedTags: {
            releaseId,
            chainId,
          },
          isArtistOnlyPage: false,
          pillar: PILLARS.MINTING,
          level: 'fatal',
        });
    }
  });

  const onGift = useStableCallback(() => {
    switch (auctionContractType) {
      case 'ARTIST':
        if (showPurchaseSecondaryListings) {
          return openModal(ModalType.RELEASE_CHECKOUT_GIFT, {
            body: (
              <MarketPlaceCheckoutModal
                key={`${releaseId}-gift-checkout-secondary`}
                releaseData={release}
                contractAddress={contractAddress}
                marketPlacePlaceUrl={marketPlaceUrl}
                openGiftingDropdown
              />
            ),
          });
        }

        return openToast({
          text: 'Gifting is not supported for this release',
          variant: 'error',
        });
      case 'EDITION':
        return openModal(ModalType.RELEASE_CHECKOUT_GIFT, {
          body: showPurchaseSecondaryListings ? (
            <MarketPlaceCheckoutModal
              key={`${releaseId}-gift-checkout-secondary`}
              releaseData={release}
              contractAddress={contractAddress}
              marketPlacePlaceUrl={marketPlaceUrl}
              openGiftingDropdown
            />
          ) : (
            <CheckoutModal
              key={`${releaseId}-gift-checkout`}
              releaseData={release}
              schedule={selectedSaleSchedule}
              headingText={heading}
              selectedPanel={selectedPanel}
              openGiftingDropdown
            />
          ),
        });
    }
  });

  //only show gifting if auth, release is supported, public panel is selected, sale has not started and sale is not sold out or ended
  const showGifting = React.useMemo(
    () =>
      (selectedSaleSchedule &&
        Object.values(ALLOWED_INTERFACES_FOR_GIFTING).includes(selectedSaleSchedule.interfaceId) &&
        selectedPanel === 'PUBLIC' &&
        !(selectedSchedule?.saleEnded || selectedSchedule?.isSoldOut)) ||
      showPurchaseSecondaryListings,
    [
      selectedPanel,
      selectedSaleSchedule,
      selectedSchedule?.isSoldOut,
      selectedSchedule?.saleEnded,
      showPurchaseSecondaryListings,
    ],
  );

  if (isLoading && !isInModal) {
    return <div className="h-52 w-full animate-pulse rounded-lg bg-neutral200 lt:max-w-sm" />;
  }

  const disabledCollect =
    !selectedSchedule?.saleStarted || selectedSchedule?.saleEnded || selectedSchedule?.isSoldOut;

  return (
    <div className="flex w-full flex-col rounded-md lt:max-w-sm">
      <div
        className={clsx(
          'w-full overflow-hidden rounded-t-lg bg-white',
          isInModal ? 'flex border-b border-base200 pb-4' : 'hidden p-4 shadow-tertiary lt:flex',
        )}
      >
        <SongInfo release={release} />
      </div>
      <div
        className={clsx(
          'z-above1 flex w-full flex-col gap-4 bg-white',
          isInModal ? 'pt-4' : 'rounded-t-lg p-4 shadow-tertiary lt:rounded-t-none',
        )}
      >
        {isLoading && isInModal ? (
          <div className="h-[106px] w-full animate-pulse rounded-lg bg-neutral200" />
        ) : isSoldOut ? (
          <>
            <PanelWrapper selected subInfo={null}>
              <div className="flex flex-col gap-1 p-3">
                <div className="flex items-center justify-between gap-2">
                  <h2 className="font-title text-title-m font-medium text-base900">{heading}</h2>
                  <SecondaryBadge
                    marketPlaceUrl={marketPlaceUrl}
                    className="cursor-pointer bg-transparent text-neutral400 hover:text-neutral800"
                  />
                </div>
                <div className="flex w-full items-center justify-between">
                  <div className="flex items-center gap-1">
                    <NetworkIndicator chainId={chainId} />
                    <span className="font-base text-base-m font-semibold">
                      {priceDisplayedToUser}
                    </span>
                  </div>
                </div>

                <div className="flex h-5 items-center justify-between">
                  <div className="flex items-center">
                    <p className="text-base-m font-medium text-base500">
                      {millify(totalMinted ?? totalMinted)}
                      {SPACE}
                      {getSingularOrPlural({
                        count: totalMinted ?? totalMinted,
                        word: 'mint',
                      })}
                    </p>
                  </div>
                  {selectedSchedule && (
                    <EditionBadges
                      selectedSchedule={selectedSchedule}
                      publicSaleCutOff={publicSaleCutOff}
                    />
                  )}
                </div>

                <div className="mt-2 flex w-full gap-2 [&_a]:w-full">
                  <CollectButtonV3
                    fullWidth
                    sourceComponent={collectButtonSource}
                    releaseId={release.id}
                    marketPlaceUrl={marketPlaceUrl}
                    isSaleOverOrUserHitLimit={isSaleOverOrUserHitLimit}
                    showDisabledCollect={disabledCollect}
                    onBuy={onBuy}
                    showPurchaseSecondaryListings={showPurchaseSecondaryListings}
                  />

                  {showGifting && isAuthenticatedWithAPI && (
                    <GiftingButton
                      releaseId={release.id}
                      sourceComponent="Mint Component"
                      onClick={onGift}
                      disabled={!showPurchaseSecondaryListings}
                      size="L"
                    />
                  )}
                </div>
              </div>
            </PanelWrapper>
          </>
        ) : (
          <>
            <PanelWrapper
              selected
              subInfo={
                selectedSchedule ? (
                  <MintComponentPanelSubInfo
                    saleDetails={selectedSchedule}
                    selectedPanel={selectedPanel}
                    hasPresale={hasPresale}
                    hasPresaleAccess={!!presaleDetails?.hasAccess}
                  />
                ) : null
              }
            >
              <div className="flex flex-col gap-1 p-3">
                <div className="flex items-center justify-between gap-2">
                  <h2 className="font-title text-title-m font-medium text-base900">{heading}</h2>
                  {hasPresale && (
                    <SaleToggle
                      valueState={selectedPanel}
                      onValueChange={setSelectedPanel}
                      presaleEligible={!!presaleDetails.hasAccess}
                    />
                  )}
                </div>

                <div className="flex w-full items-center justify-between">
                  <div className="flex items-center gap-1">
                    <NetworkIndicator chainId={chainId} />
                    <span className="font-base text-base-m font-semibold">
                      {priceDisplayedToUser}
                    </span>
                  </div>
                </div>

                <div className="flex h-5 items-center justify-between">
                  <div className="flex items-center">
                    <p className="text-base-m font-medium text-base500">
                      {isOpenEdition ||
                      selectedSchedule?.isSoldOut ||
                      selectedSchedule?.saleEnded ? (
                        <>
                          {millify(totalMinted ?? totalMinted)}
                          {SPACE}
                          {getSingularOrPlural({
                            count: totalMinted ?? totalMinted,
                            word: 'mint',
                          })}
                        </>
                      ) : (
                        <>
                          {totalMinted}/{maxSupply}
                          {SPACE}
                          {getSingularOrPlural({
                            count: totalMinted ?? totalMinted,
                            word: 'mint',
                          })}
                        </>
                      )}
                    </p>
                    {isAuthenticatedWithAPI && !!presaleDetails && selectedPanel === 'PRESALE' && (
                      <div className="flex items-center justify-center text-base-m font-medium text-base500">
                        {SPACE}•{SPACE}
                        {!!presaleDetails ? (
                          <Button
                            variant="text"
                            size="XS"
                            label="Eligible"
                            className="m-0 h-auto min-w-0 p-0 text-fuchsia500"
                            onClick={openAllowlistModal}
                          />
                        ) : (
                          <Button
                            variant="text"
                            size="XS"
                            label="Not Eligible"
                            className="m-0 h-auto min-w-0 p-0 text-base500"
                            onClick={openAllowlistModal}
                          />
                        )}
                      </div>
                    )}
                  </div>
                  {selectedSchedule && (
                    <EditionBadges
                      selectedSchedule={selectedSchedule}
                      publicSaleCutOff={publicSaleCutOff}
                    />
                  )}
                </div>

                <div className="mt-2 flex w-full gap-2 [&_a]:w-full">
                  <CollectButtonV3
                    fullWidth
                    sourceComponent={collectButtonSource}
                    releaseId={release.id}
                    marketPlaceUrl={marketPlaceUrl}
                    isSaleOverOrUserHitLimit={isSaleOverOrUserHitLimit}
                    showDisabledCollect={disabledCollect}
                    onBuy={onBuy}
                    showPurchaseSecondaryListings={showPurchaseSecondaryListings}
                  />

                  {showGifting && isAuthenticatedWithAPI && (
                    <GiftingButton
                      releaseId={release.id}
                      sourceComponent="Mint Component"
                      onClick={onGift}
                      disabled={disabledCollect}
                      size="L"
                    />
                  )}
                </div>
              </div>
            </PanelWrapper>
          </>
        )}
      </div>

      {!isInModal && <SocialProofBar releaseId={release.id} totalMinted={totalMinted} />}
    </div>
  );
}

const getMintInfoText = ({
  isOpenEdition,
  isFixedEdition,
  isSamEnabled,
}: {
  isOpenEdition: boolean;
  isFixedEdition: boolean;
  isSamEnabled: boolean;
}) => {
  /**
   * ordering here is important
   * isFixedEdition check handles expired Sound Swap that turned into Fixed
   * isOpenEdition can be true for both OE and SS so it should be checked last
   */
  if (isFixedEdition) {
    return MINT_COMPONENT_HEADING_SUBTEXT.FIXED;
  }

  if (isSamEnabled) return MINT_COMPONENT_HEADING_SUBTEXT.SAM;

  if (isOpenEdition) {
    return MINT_COMPONENT_HEADING_SUBTEXT.OPEN;
  }

  return MINT_COMPONENT_HEADING_SUBTEXT.FALLBACK;
};
